import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import type { TrainingsFilters, TrainingModule } from "@/types";
import type { Schemas } from "@/types/api";



// ========== Training Queries ==========

export const useTrainings = (filters?: TrainingsFilters) => {
  return useQuery({
    queryKey: ["trainings", filters],
    queryFn: () =>
      api.get("/trainings/", {
        query: {
          active_only: filters?.active_only,
          category_id: filters?.category_id || undefined,
          department_id: filters?.department_id || undefined,
          search: filters?.search || undefined,
        },
      }),
  });
};

export const useTraining = (id: string) => {
  return useQuery({
    queryKey: ["trainings", id],
    queryFn: async () => {
      return api.get("/trainings/{training_id}", { path: { training_id: id } });
    },
    enabled: !!id,
  });
};

export const useTrainingCompletions = (trainingId: string) => {
  return useQuery({
    queryKey: ["training-completions", trainingId],
    queryFn: async () => {
      return api.get("/trainings/{training_id}/completions", { path: { training_id: trainingId } });
    },
    enabled: !!trainingId,
  });
};

export const useMyTrainings = () => {
  return useQuery({
    queryKey: ["trainings", "my-trainings"],
    queryFn: async () => {
      return api.get("/trainings/my-trainings");
    },
  });
};

export const useTrainingStats = () => {
  return useQuery({
    queryKey: ["trainings", "stats"],
    queryFn: async () => {
      return api.get("/trainings/stats");
    },
  });
};

export const useTrainingCategories = () => {
  return useQuery({
    queryKey: ["training-categories"],
    queryFn: async () => {
      return api.get("/trainings/categories");
    },
  });
};

export const useContentBlocks = (parentType: "training" | "article", parentId: string) => {
  return useQuery({
    queryKey: ["content-blocks", parentType, parentId],
    queryFn: () =>
      parentType === "training"
        ? api.get("/trainings/{training_id}/blocks", { path: { training_id: parentId } })
        : api.get("/knowledge-base/{article_id}/blocks", { path: { article_id: parentId } }),
    enabled: !!parentId,
  });
};

export const useTrainingTimeReport = (
  year?: number,
  departmentId?: string,
  userId?: string,
  trainingId?: string,
) => {
  const effectiveYear = year ?? new Date().getFullYear();
  return useQuery({
    queryKey: ["training-time-report", effectiveYear, departmentId, userId, trainingId],
    queryFn: () =>
      api.get("/trainings/time/report", {
        query: { year: effectiveYear, department_id: departmentId || undefined, user_id: userId || undefined, training_id: trainingId || undefined },
      }),
  });
};

export type ProgressReportRow = Schemas["ProgressReportRow"];

export const useTrainingProgressReport = (departmentId?: string, trainingId?: string) => {
  return useQuery({
    queryKey: ["training-progress-report", departmentId, trainingId],
    queryFn: () =>
      api.get("/trainings/progress/report", { query: { department_id: departmentId || undefined, training_id: trainingId || undefined } }),
  });
};

export const useTrainingMonthlyAnalyticsMultiYear = (years: number[]) => {
  return useQuery({
    queryKey: ["training-monthly-analytics-multi", years],
    queryFn: async () => {
      const results = await Promise.all(
        years.map((year) =>
          api.get("/trainings/analytics/monthly", { query: { year: year } }).then((data) => data.map((d) => ({ ...d, year })))
        )
      );
      return results.flat();
    },
    enabled: years.length > 0,
  });
};

export const useQuizQuestions = (blockId: string) => {
  return useQuery({
    queryKey: ["quiz-questions", blockId],
    queryFn: async () => {
      return api.get("/trainings/blocks/{block_id}/questions", { path: { block_id: blockId } });
    },
    enabled: !!blockId,
  });
};

export const useQuizAttempts = (blockId: string) => {
  return useQuery({
    queryKey: ["quiz-attempts", blockId],
    queryFn: async () => {
      return api.get("/trainings/blocks/{block_id}/attempts", { path: { block_id: blockId } });
    },
    enabled: !!blockId,
  });
};

export const useAllBlockAttempts = (blockId: string) => {
  return useQuery({
    queryKey: ["quiz-attempts-all", blockId],
    queryFn: async () => {
      return api.get("/trainings/blocks/{block_id}/attempts/all", { path: { block_id: blockId } });
    },
    enabled: !!blockId,
  });
};

export const useOnboardingStatus = () => {
  return useQuery({
    queryKey: ["onboarding-status"],
    queryFn: async () => {
      return api.get("/trainings/onboarding/check");
    },
  });
};

export const useTrainingModules = (trainingId: string, initialData?: TrainingModule[]) => {
  return useQuery({
    queryKey: ["training-modules", trainingId],
    queryFn: async () => {
      return api.get("/trainings/{training_id}/modules", { path: { training_id: trainingId } });
    },
    enabled: !!trainingId,
    staleTime: 0,
    initialData: initialData && initialData.length > 0 ? initialData : undefined,
  });
};

export const useTrainingProgress = (trainingId: string) => {
  return useQuery({
    queryKey: ["training-progress", trainingId],
    queryFn: async () => {
      return api.get("/trainings/{training_id}/progress", { path: { training_id: trainingId } });
    },
    enabled: !!trainingId,
  });
};

export const useTrainingModuleCompletions = (trainingId: string) => {
  return useQuery({
    queryKey: ["training-module-completions", trainingId],
    queryFn: async () =>
      api.get("/trainings/{training_id}/module-completions", { path: { training_id: trainingId } }),
    enabled: !!trainingId,
  });
};

export const useLessonContentBlocks = (lessonId: string) => {
  return useQuery({
    queryKey: ["content-blocks", "lesson", lessonId],
    queryFn: async () => {
      return api.get("/trainings/lessons/{lesson_id}/blocks", { path: { lesson_id: lessonId } });
    },
    enabled: !!lessonId,
    staleTime: 0,
    gcTime: 0,
  });
};
